import { useSlate } from "slate-react";
import { Editor, Transforms, Element as SlateElement, Range, Text } from "slate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { CustomTextFormat, StylesFormatting } from "../slate-types";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { getFontSizeForBlockType } from "./HeadingSelect";

const LIST_TYPES = ["ol", "ul"];
const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"];
export const toggleBlock = (editor: Editor, format) => {
  const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? "align" : "type");
  const isList = LIST_TYPES.includes(format);
  const [imageElement] = Array.from(
    Editor.nodes(editor, { at: editor.selection, match: (n) => SlateElement.isElement(n) && n.type === "img" })
  );
  if (imageElement) {
    return;
  }
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });
  let newProperties: Partial<SlateElement>;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? "paragraph" : isList ? "li" : format,
      fontSize: getFontSizeForBlockType(format),
    };
  }
  const marks = Editor.marks(editor);
  if (marks.color) {
    newProperties.color = marks.color;
  }
  if (marks.fontFamily) {
    newProperties.fontFamily = marks.fontFamily;
  }
  Transforms.setNodes<SlateElement>(editor, newProperties, { at: editor.selection });
  if (isActive) {
    Transforms.unwrapNodes(editor, {
      at: editor.selection,
      match: (n) => SlateElement.isElement(n) && n.type === format,
    });
  }
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const toggleMark = (editor: Editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const isBlockActive = (editor: Editor, format, blockType = "type") => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format,
    })
  );

  return !!match;
};

const isMarkActive = (editor: Editor, format: keyof CustomTextFormat) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const increaseIndent = (editor: Editor) => {
  // Ensure that there is a valid selection (cursor position)
  if (!editor.selection || !Range.isCollapsed(editor.selection)) {
    return; // Do nothing if there's no selection or if the selection is not collapsed (single cursor position)
  }
  const nodes = Array.from(
    Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === "li",
      at: editor.selection,
    })
  );
  const listItem = nodes[0];
  if (!listItem) {
    return;
  }
  const previousSibling = Editor.previous(editor, { at: listItem[1] });
  if (previousSibling) {
    const parentList = Editor.parent(editor, listItem[1]);

    if (parentList && SlateElement.isElement(parentList[0])) {
      const parentListType: SlateElement["type"] = parentList[0].type; // 'ul' or 'ol'

      // Replace the content of the current list item with the new nested list
      Transforms.wrapNodes(
        editor,
        {
          type: parentListType as "ul" | "ol", // Inherit the type of the parent list
          children: [{ type: "li", children: [] }], // New nested list item
        },
        { at: listItem[1], split: true } // Wrap at the current list item path and split its content
      );
    }
  }
};

export const isDisabledIndent = (editor: Editor) => {
  const { selection } = editor;
  if (!selection) return false;
  const [liElement] = Array.from(
    Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === "li",
      at: editor.selection,
    })
  );
  if (liElement) {
    const previousSibling = Editor.previous(editor, { at: liElement[1] });
    const parent = Editor.parent(editor, liElement[1]);
    return !(
      previousSibling &&
      SlateElement.isElement(previousSibling[0]) &&
      (previousSibling[0].type === "li" ||
        (parent?.[0].children && (previousSibling[0].type === "ol" || previousSibling[0].type === "ul")))
    );
  } else {
    return true;
  }
};
export const BlockButton = ({ format, icon, title }: { format: string; title?: string; icon: IconName }) => {
  const editor = useSlate();
  return (
    <div
      title={title}
      className={classNames([
        "slate--mark-btn",
        { ["mark-active"]: isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? "align" : "type") },
      ])}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      <FontAwesomeIcon icon={["far", icon]} fixedWidth />
    </div>
  );
};

export const MarkButton = ({
  format,
  icon,
  title,
}: {
  format: keyof CustomTextFormat;
  icon: IconName;
  title?: string;
}) => {
  const editor = useSlate();
  return (
    <div
      title={title}
      className={classNames(["slate--mark-btn", { ["mark-active"]: isMarkActive(editor, format) }])}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      <FontAwesomeIcon icon={["far", icon]} fixedWidth />
    </div>
  );
};

export const IndentButton = () => {
  const editor = useSlate();
  const { t } = useTranslation(I18N_NS.RICH_EDITOR);

  return (
    <div
      title={t("Indent")}
      className={classNames(["slate--mark-btn", { ["mark-disabled"]: isDisabledIndent(editor) }])}
      onMouseDown={(event) => {
        event.preventDefault();
        if (!isDisabledIndent(editor)) {
          increaseIndent(editor);
        }
      }}
    >
      <FontAwesomeIcon icon={["far", "indent"]} fixedWidth />
    </div>
  );
};

export const decreaseIndent = (editor: Editor) => {
  const matches = Array.from(
    Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === "li",
      at: editor.selection,
    })
  );

  if (matches.length) {
    const liMatch = matches[0];
    const parentList = Editor.parent(editor, liMatch[1]);
    const parentOfParent = Editor.parent(editor, parentList[1]);

    // Check if this is the last level of nesting
    const isLastLevel = !(
      SlateElement.isElement(parentOfParent[0]) &&
      (parentOfParent[0].type === "ol" || parentOfParent[0].type === "ul")
    );

    if (parentList && SlateElement.isElement(parentList[0])) {
      const parentListType: SlateElement["type"] = parentList[0].type;

      // If this is the last level, convert to paragraph
      if (isLastLevel) {
        // First unwrap from the list
        Transforms.unwrapNodes(editor, {
          match: (n) => SlateElement.isElement(n) && n.type === parentListType,
          split: true,
          at: liMatch[1],
        });

        // Then convert the li to paragraph
        Transforms.setNodes(
          editor,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { type: "paragraph" as any },
          {
            match: (n) => SlateElement.isElement(n) && n.type === "li",
            at: editor.selection,
          }
        );
      } else {
        // Just unwrap from the current list level
        Transforms.unwrapNodes(editor, {
          match: (n) => SlateElement.isElement(n) && n.type === parentListType,
          split: true,
          at: liMatch[1],
        });
      }
    }
  }
};
export const isDisabledOutdent = (editor: Editor) => {
  const { selection } = editor;
  if (!selection) return true;

  const [liElement] = Array.from(
    Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === "li",
      at: editor.selection,
    })
  );

  if (!liElement) return true;

  const parentList = Editor.parent(editor, liElement[1]);
  if (!parentList) return true;

  return false;
};

export const OutdentButton = () => {
  const editor = useSlate();
  const { t } = useTranslation(I18N_NS.RICH_EDITOR);

  return (
    <div
      className={classNames(["slate--mark-btn", { ["mark-disabled"]: isDisabledOutdent(editor) }])}
      onMouseDown={(event) => {
        event.preventDefault();
        if (!isDisabledOutdent(editor)) {
          decreaseIndent(editor);
        }
      }}
      title={t("Outdent")}
    >
      <FontAwesomeIcon icon={["far", "outdent"]} fixedWidth />
    </div>
  );
};

export const ClearFormatting = () => {
  const editor = useSlate();
  const { t } = useTranslation(I18N_NS.RICH_EDITOR);

  const clearFormatting = () => {
    const props: Array<keyof CustomTextFormat | keyof StylesFormatting> = [
      "bold",
      "code",
      "color",
      "fontSize",
      "fontFamily",
      "italic",
      "strikethrough",
      "underline",
      "align",
      "background",
    ];

    const textNodes = Array.from(
      Editor.nodes(editor, {
        at: editor.selection,
        mode: "all",
        match: (n) => SlateElement.isElement(n) || Text.isText(n),
      })
    );
    if (textNodes.length) {
      Transforms.unsetNodes(editor, props, {
        at: editor.selection,
        mode: "all",
        match: (n) => Text.isText(n),
        split: true,
      });
    } else {
      Transforms.unsetNodes(editor, props, {
        at: editor.selection,
        mode: "all",
        match: (n) => SlateElement.isElement(n) || Text.isText(n),
      });
    }

    Transforms.setNodes(
      editor,
      { type: "paragraph" },
      {
        at: [],
        mode: "all",
        match: (n) => SlateElement.isElement(n) && /^h[1-6]$/.test(n.type),
      }
    );
  };

  return (
    <div
      title={t("Clear formatting")}
      className={"slate--mark-btn"}
      onMouseDown={(event) => {
        event.preventDefault();
        clearFormatting();
      }}
    >
      <FontAwesomeIcon icon={["far", "remove-format"]} fixedWidth />
    </div>
  );
};

export const ToolsDivider = () => <div className="slate--divider" />;
